import React, { useState, useRef, useEffect } from "react";
import EXIF from "exif-js";
import * as Sentry from "@sentry/react";

import { useFilesContext } from "../../context/domain/files/FilesContext";
import { ENV_CONFIG } from "../../config/apiConfig";
import { FILE_CONFIG } from "../../config/constants";
import { validateFileType } from "../../utils/files/fileTypeDetection";

import { handleFiles } from "../../services/api/core";

import { PictureUploads } from "../../components/ui";
import { VideoUpload } from "../../components/ui";

import { formatDate } from "../../utils/datetime";
import { convert24To12String } from "../../utils/datetime";

import { Button } from "../../components/ui";
import { MessageModal } from "../../components/ui";

/**
 * Component for uploading job media (pictures and videos).
 *
 * @param {Object} props - Component props.
 * @param {number|string|null} [props.initialProject=null] - Job primary key to lock;
 *   when set, hides the job dropdown and assumes that job.
 * @param {string} [props.title="Upload Media (Images/Video)"] - Page heading;
 *   override when embedding.
 * @returns {JSX.Element}
 */
const UploadMedia = ({
  initialProject = null,
  title = "Upload Media (Images/Video)",
}) => {
  const { jobs } = useFilesContext();
  const isJobLocked = initialProject != null;
  const [selectedJob, setSelectedJob] = useState("");
  const [files, setFiles] = useState([]);
  const [fileCount, setFileCount] = useState(0);
  const fileInputRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isJobLocked) {
      document.title = "R&S Media Upload";
    }
  }, [isJobLocked]);

  // Lock selected job when embedding with a project id.
  useEffect(() => {
    if (!isJobLocked || !jobs?.length) {
      return;
    }
    const job = jobs.find((item) => item.id === Number(initialProject));
    if (job) {
      setSelectedJob(job);
    }
  }, [isJobLocked, initialProject, jobs]);

  /**
   * Builds an image caption from EXIF date and reverse-geocoded or job address.
   *
   * @param {Object} picture Staged picture object (mutates `addressString`).
   * @returns {string} Caption text.
   */
  const generateCaption = (picture) => {
    const dateCreated = picture.originalDate
      ? `${formatDate(new Date(picture.originalDate.split(" ")[0].replaceAll(":", "/")), "month DD, YYYY")} at ${convert24To12String(picture.originalDate.split(" ")[1])}`
      : new Date(picture.file.lastModified).toLocaleString();

    if (picture.address === "No address available") {
      picture.addressString = `${selectedJob.default_address ? selectedJob.default_address.address : "No address for job in DB, please enter manually."}`;
      return `${dateCreated}\n${picture.addressString}`;
    } else {
      const address = picture.address;
      picture.addressString = `${address.amenity ? address.amenity + ",\n" : ""}${address.house_number ? address.house_number + ", " : ""}${address.road ? address.road + ",\n" : ""}${address.town ? address.town + ", " : ""}${address.city ? address.city + ", " : ""}${address.county ? address.county + ",\n" : ""}${address.state ? address.state + ", " : ""}${address.postcode ? address.postcode : ""}`;
      if (picture.addressString === "") {
        // Fallback for complete failure to get address
        picture.addressString = `${selectedJob.default_address ? selectedJob.default_address.address : "No address for job in DB, please enter manually."}`;
      }
      return `${dateCreated}\n${picture.addressString}`;
    }
  };

  /**
   * Formats a Date or epoch ms as `YYYY-MM-DD`.
   *
   * @param {Date|number} date Date value.
   * @returns {string|undefined} Date key.
   */
  const formatMediaDate = (date) => {
    if (typeof date === "number") {
      date = new Date(date);
    }
    if (typeof date === "object" && date instanceof Date) {
      return formatDate(date, "YYYY-MM-DD");
    }
  };

  /**
   * Sets the selected job and regenerates image captions.
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} e Job select change.
   * @returns {void}
   */
  const handleJobChange = (e) => {
    const jobId = Number(e.target.value);
    const job = jobs.find((job) => job.id === jobId);
    setSelectedJob(job);

    // Update captions for images only (videos don't have captions)
    setFiles((prevFiles) =>
      prevFiles.map((fileObj) => {
        if (fileObj.fileType === "image") {
          return {
            ...fileObj,
            caption: generateCaption(fileObj),
          };
        }
        return fileObj;
      })
    );
  };

  /**
   * Converts EXIF DMS coordinates to decimal degrees.
   *
   * @param {number} degrees Degrees.
   * @param {number} minutes Minutes.
   * @param {number} seconds Seconds.
   * @param {string} direction `N`/`S`/`E`/`W`.
   * @returns {number} Decimal degrees (south/west negative).
   */
  const ConvertDMSToDD = (degrees, minutes, seconds, direction) => {
    let dd = degrees + minutes / 60 + seconds / 3600;
    if (direction === "S" || direction === "W") {
      dd = dd * -1;
    }
    return dd;
  };

  /**
   * Reverse-geocodes lat/lon via the maps.co API.
   *
   * @param {number} lat Latitude.
   * @param {number} lon Longitude.
   * @returns {Promise<Object|string>} Address object or fallback string.
   */
  const getAddressFromCoordinates = async (lat, lon) => {
    const apiKey = ENV_CONFIG.GEOCODE_API_KEY;
    const response = await fetch(
      `https://geocode.maps.co/reverse?lat=${lat}&lon=${lon}&api_key=${apiKey}`
    );
    const data = await response.json();
    if (data) {
      return data.address;
    }
    return "No address found";
  };

  /**
   * Validates selected files, reads image EXIF, and stages them for upload.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e File input change.
   * @returns {void}
   */
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setErrorMessage(""); // Clear any previous error messages

    // Validate and categorize files
    selectedFiles.forEach((file) => {
      const validation = validateFileType(file);

      if (!validation.valid) {
        setErrorMessage(validation.error);
        console.error(validation.error);
        return;
      }

      const fileType = validation.type;

      // Validate file size
      if (fileType === "video" && file.size > FILE_CONFIG.MAX_VIDEO_SIZE) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
        const maxSizeMB = (FILE_CONFIG.MAX_VIDEO_SIZE / (1024 * 1024)).toFixed(
          0
        );
        const error = `Video file "${file.name}" is too large (${sizeMB}MB). Maximum size is ${maxSizeMB}MB.`;
        setErrorMessage(error);
        console.error(error);
        return;
      }

      if (fileType === "image" && file.size > FILE_CONFIG.MAX_FILE_SIZE) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
        const maxSizeMB = (FILE_CONFIG.MAX_FILE_SIZE / (1024 * 1024)).toFixed(
          0
        );
        const error = `Image file "${file.name}" is too large (${sizeMB}MB). Maximum size is ${maxSizeMB}MB.`;
        setErrorMessage(error);
        console.error(error);
        return;
      }

      // Base file object for both images and videos
      const fileObj = {
        file,
        fileType,
        comment: "",
        caption: "",
        isCaptionEnabled: true,
        originalDate: "",
        gps: "No GPS data available",
        address: "No address available",
        addressString: "No address available",
      };

      if (fileType === "image") {
        // Process EXIF data for images
        EXIF.getData(file, async function () {
          const dateTime = EXIF.getTag(this, "DateTimeOriginal");
          const lat = EXIF.getTag(this, "GPSLatitude");
          const lon = EXIF.getTag(this, "GPSLongitude");
          const latRef = EXIF.getTag(this, "GPSLatitudeRef");
          const lonRef = EXIF.getTag(this, "GPSLongitudeRef");

          if (lat && lon && latRef && lonRef) {
            const latDecimal = ConvertDMSToDD(lat[0], lat[1], lat[2], latRef);
            const lonDecimal = ConvertDMSToDD(lon[0], lon[1], lon[2], lonRef);
            fileObj.gps = `Lat: ${latDecimal}, Lon: ${lonDecimal}`;
            fileObj.address = await getAddressFromCoordinates(
              latDecimal,
              lonDecimal
            );
          } else {
            fileObj.gps = "No GPS data available";
            fileObj.address = "No address available";
          }

          if (dateTime) {
            fileObj.originalDate = dateTime;
          } else {
            // Fallback to the file's last modified date if DateTimeOriginal is missing
            fileObj.originalDate = formatMediaDate(file.lastModifiedDate);
          }

          fileObj.caption = generateCaption(fileObj);
          setFiles((prevFiles) => [...prevFiles, fileObj]);
        });
      } else if (fileType === "video") {
        // Videos: no EXIF processing, just add with basic metadata
        fileObj.originalDate = formatMediaDate(file.lastModifiedDate);
        // Videos don't have captions overlaid, so leave empty
        fileObj.caption = "";
        fileObj.isCaptionEnabled = false;
        setFiles((prevFiles) => [...prevFiles, fileObj]);
      }
    });

    // Reset the file input to allow re-selection of the same file
    e.target.value = null;
  };

  /**
   * Simulates a short upload progress bar so the user is not blocked on the request.
   *
   * @returns {Promise<void>}
   */
  const startUploadProgress = async () => {
    // This function is moditified to simulate upload progress and assume success so
    // that the user does not have to wait for the upload to complete.
    const totalTime = 500;
    const intervalTime = 100; // In milliseconds
    const increment = (100 / totalTime) * intervalTime; // Calculate progress increment per interval
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + increment; // Increment progress based on calculated value
      });
    }, intervalTime);
    await new Promise((resolve) => setTimeout(resolve, totalTime)); // Wait for totalTime
    setIsUploading(false); // Set uploading state to false after upload is complete
  };

  /**
   * Updates a staged file's comment.
   *
   * @param {number} index File index.
   * @param {string} comment Comment text.
   * @returns {void}
   */
  const handlePictureCommentChange = (index, comment) => {
    const newFiles = [...files];
    newFiles[index].comment = comment;
    setFiles(newFiles);
  };

  /**
   * Updates a staged image caption.
   *
   * @param {number} index File index.
   * @param {string} caption Caption text.
   * @returns {void}
   */
  const handlePictureCaptionChange = (index, caption) => {
    const newFiles = [...files];
    newFiles[index].caption = caption;
    setFiles(newFiles);
  };

  /**
   * Toggles whether an image caption is enabled.
   *
   * @param {number} index File index.
   * @returns {void}
   */
  const handleCaptionToggle = (index) => {
    const newFiles = [...files];
    newFiles[index].isCaptionEnabled = !newFiles[index].isCaptionEnabled;
    setFiles(newFiles);
  };

  /**
   * Date taken from EXIF, or last-modified when EXIF is unavailable.
   *
   * @param {Object} picture Staged picture.
   * @returns {string} `YYYY-MM-DD` date key.
   */
  const getPictureTakenDate = (picture) => {
    if (picture.originalDate) {
      return formatDate(
        new Date(picture.originalDate.split(" ")[0].replaceAll(":", "/")),
        "YYYY-MM-DD"
      );
    } else {
      // If we dont have access to date created due to browser security restrictions
      // we use the last modified date instead as it is usually analogous.
      return formatMediaDate(picture.file.lastModified);
    }
  };

  /**
   * Uploads staged images and videos for the selected job.
   *
   * @returns {Promise<void>}
   */
  const handleSubmit = async () => {
    setIsUploading(true);
    setIsModalOpen(true);
    startUploadProgress();

    // Separate images and videos
    const imageFiles = files.filter((file) => file.fileType === "image");
    const videoFiles = files.filter((file) => file.fileType === "video");

    try {
      // Upload images first
      if (imageFiles.length > 0) {
        await handleFiles({
          files: imageFiles.map((picture) => {
            return {
              file: picture.file,
              type: "image",
            };
          }),
          action: "upload_job_pictures",
          additional_data: {
            job_id: selectedJob.id,
            picture_data: JSON.stringify(
              imageFiles.map((picture) => {
                return {
                  filename: picture.file.name,
                  date: getPictureTakenDate(picture),
                  caption: picture.caption,
                  isCaptionEnabled: picture.isCaptionEnabled,
                  comment: picture.comment,
                  gps: picture.gps,
                  address: picture.address,
                  addressString: picture.addressString,
                };
              })
            ),
          },
        });
      }

      // Upload videos
      if (videoFiles.length > 0) {
        await handleFiles({
          files: videoFiles.map((video) => {
            return {
              file: video.file,
              type: "video",
            };
          }),
          action: "upload_job_videos",
          additional_data: {
            job_id: selectedJob.id,
            video_data: JSON.stringify(
              videoFiles.map((video) => {
                return {
                  filename: video.file.name,
                  date: formatMediaDate(video.file.lastModified),
                  comment: video.comment,
                };
              })
            ),
          },
        });
      }
    } catch (error) {
      console.error("Upload failed:", error);

      // Capture error in Sentry with context for debugging
      Sentry.captureException(error, {
        tags: {
          upload_type: imageFiles.length > 0 && videoFiles.length > 0 ? 'mixed' :
                       videoFiles.length > 0 ? 'video' : 'image',
          job_id: selectedJob.id,
          file_count: files.length,
        },
        extra: {
          job_number: selectedJob.number,
          job_description: selectedJob.description,
          image_count: imageFiles.length,
          video_count: videoFiles.length,
          file_names: files.map(f => f.file.name),
          file_sizes: files.map(f => f.file.size),
          total_size_mb: (files.reduce((sum, f) => sum + f.file.size, 0) / (1024 * 1024)).toFixed(2),
        }
      });

      setUploadProgress(0);
      setIsUploading(false);
      setIsModalOpen(true);

      // Handle specific error cases
      if (error.code === "ECONNABORTED") {
        setUploadProgress(() => ({
          errorMessage: `Upload failed: Request timed out. Please try again. ${error.message}`,
        }));
      } else {
        setUploadProgress(() => ({
          errorMessage: `Upload failed: ${error.message || "Unknown error"}`,
        }));
      }
    } finally {
      if (!uploadProgress.errorMessage) {
        // For now we are assuming success to prevent user waiting for uploads.
        // setIsUploading(false); // Set uploading state to false
      }
    }
  };

  /**
   * Closes the upload modal and clears staged files (keeps locked job when embedded).
   *
   * @returns {void}
   */
  const handleUploadedModalClose = () => {
    setIsModalOpen(false);
    setFiles([]);
    setFileCount(0);
    // Keep the locked project job when embedded; reset only for standalone upload.
    if (!isJobLocked) {
      setSelectedJob("");
      const jobSelect = document.getElementById("job");
      if (jobSelect) {
        jobSelect.selectedIndex = 0;
      }
    }
  };

  /**
   * Opens the hidden file input.
   *
   * @returns {void}
   */
  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    setFileCount(files.length);
  }, [files]);

  return (
    <>
      <div
        className={
          isJobLocked
            ? "font-inter"
            : "min-h-screen bg-custom-rsbg font-inter"
        }
      >
        <div className={isJobLocked ? undefined : "container mx-auto p-4"}>
          <h1
            className={`mb-2 text-2xl font-bold text-gray-950 ${
              isJobLocked ? "mt-0" : "mt-8"
            }`}
          >
            {title}
          </h1>
          <div
            className={
              isJobLocked
                ? undefined
                : "bg-white p-6 rounded-lg shadow-md"
            }
          >
            {!isJobLocked && (
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-bold mb-2"
                  htmlFor="job"
                >
                  Job:
                </label>
                <select
                  id="job"
                  value={selectedJob.id}
                  onChange={handleJobChange}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                >
                  <option value="">Select a job</option>
                  {jobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.number} {job.description}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <div className="mb-4">
                <input
                  id="media"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*,video/mp4,video/quicktime,video/webm"
                />
                <div className="flex flex-row items-center">
                  <Button
                    onClick={handleBrowseClick}
                    icon="plus"
                    text="Add Media"
                    variant={selectedJob === "" ? "disabled" : "primary"}
                    className="w-32 text-white"
                  />
                  <p className="text-gray-600 ml-4 text-sm italic">
                    {fileCount > 0 ? fileCount : 0} media file(s) added.
                  </p>
                </div>
                {errorMessage && (
                  <div className="mt-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    <p className="text-sm font-semibold">{errorMessage}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {files.map((fileObj, index) => {
                // Render different components based on file type
                if (fileObj.fileType === "image") {
                  return (
                    <PictureUploads
                      key={index}
                      picture={fileObj}
                      job={selectedJob}
                      onClose={() => {
                        const newFiles = [...files];
                        newFiles.splice(index, 1);
                        setFiles(newFiles);
                        setFileCount(fileCount - 1);
                      }}
                      onCommentChange={(comment) =>
                        handlePictureCommentChange(index, comment)
                      }
                      onCaptionChange={(caption) =>
                        handlePictureCaptionChange(index, caption)
                      }
                      onCaptionToggle={() => handleCaptionToggle(index)}
                    />
                  );
                } else if (fileObj.fileType === "video") {
                  return (
                    <VideoUpload
                      key={index}
                      video={fileObj}
                      onClose={() => {
                        const newFiles = [...files];
                        newFiles.splice(index, 1);
                        setFiles(newFiles);
                        setFileCount(fileCount - 1);
                      }}
                      onCommentChange={(comment) =>
                        handlePictureCommentChange(index, comment)
                      }
                    />
                  );
                }
                return null;
              })}
            </div>
            <div className="flex items-center justify-between">
              <Button
                onClick={() => {
                  setFiles([]);
                  setFileCount(0);
                }}
                text="Clear All Media"
                variant={files.length === 0 ? "disabled" : "warning"}
                className="w-32"
              />
              <Button
                onClick={handleSubmit}
                text={`Upload ${files.length !== 0 ? files.length : ""} Media Files`}
                variant={
                  files.length === 0 || selectedJob === ""
                    ? "disabled"
                    : "secondary"
                }
                disabled={files.length === 0 || selectedJob === ""}
              />
            </div>
          </div>
        </div>
      </div>
      <MessageModal
        isOpen={isModalOpen}
        title={isUploading ? "Uploading..." : "Upload Complete"}
        message={
          isUploading ? (
            <div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-blue-500 h-4 rounded-full"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          ) : (
            "All media has been sent to the RSCMINC file server and will be available shortly."
          )
        }
        disableOK={isUploading} // Disable OK button during upload
        onClose={!isUploading ? handleUploadedModalClose : () => {}} // Disable close during upload
      />
    </>
  );
};

export default UploadMedia;
